import { Suspense } from 'react';
import ProtectedView from '@/features/auth/components/ProtectedView';
import { useNavigation } from '@/app/navigation/use-navigation';
import { PageShell } from '@/app/layout/PageShell';
import { RouteFallback } from './RouteFallback';
import { RoutingErrorBoundary } from './RoutingErrorBoundary';
import { viewRegistry } from './view-registry';

/**
 * Dispatches the current view to its registered adapter. Resolution order:
 * 1. Read `view` from NavigationContext
 * 2. Look up entry in viewRegistry
 * 3. Wrap in RoutingErrorBoundary + Suspense (for lazy chunk + render errors)
 * 4. Wrap in <ProtectedView> when the entry declares a guard
 * 5. Wrap in <PageShell> unless the entry opts into full-bleed layout
 *
 * Missing registry entries throw — view-registry.ts's dev-time assertion
 * catches this at app boot; the runtime throw is the last-line defense.
 */
export function Router() {
  const { view, actions } = useNavigation();
  const entry = viewRegistry[view];

  if (!entry) {
    throw new Error(`[Router] No registry entry for view: ${view as string}`);
  }

  const AdapterComponent = entry.component;
  const { guard, layout = 'default' } = entry;

  const inner = (
    <RoutingErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <AdapterComponent />
      </Suspense>
    </RoutingErrorBoundary>
  );

  const content = layout === 'full-bleed' ? inner : <PageShell>{inner}</PageShell>;

  if (!guard.screen && !guard.permission) {
    return content;
  }

  // A view on the other channel (JOSH-91) shows the "use the app" notice
  // instead of a denial; ProtectedView never calls onAccessDenied for it, so
  // its own "Voltar ao Início" is the way back.
  return (
    <ProtectedView
      screen={guard.screen}
      permission={guard.permission}
      onAccessDenied={() => actions.resetToHome()}
      onVoltarAoInicio={() => actions.resetToHome()}
    >
      {content}
    </ProtectedView>
  );
}
